import type { ReactNode } from "react"
import { useEffect, useId, useRef } from "react"
import { createPortal } from "react-dom"
import { CloseIcon } from "../icons"
import { useZIndex } from "@shared/hooks/useZIndex"

const DEFAULT_MODAL_ROOT_ID = "modal"
const focusableSelectors = [
  "a[href]",
  "area[href]",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[contenteditable]",
  "[tabindex]:not([tabindex='-1'])",
].join(",")

type CPopupSize = "sm" | "md" | "lg" | "xl" | "2xl" | "fit"

const sizeClasses: Record<CPopupSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  fit: "w-fit max-w-[90vw]",
}

export interface CPopupProps {
  isOpen: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: CPopupSize
  dismissible?: boolean
  closeOnBackdropClick?: boolean
  closeOnEscape?: boolean
  showCloseButton?: boolean
  overlayClassName?: string
  containerClassName?: string
  panelClassName?: string
  headerClassName?: string
  bodyClassName?: string
  footerClassName?: string
  rootId?: string
}

const joinClasses = (...values: (string | undefined | false)[]) => values.filter(Boolean).join(" ")

const getFocusableElements = (element: HTMLElement | null) => {
  if (!element) return []
  return Array.from(element.querySelectorAll<HTMLElement>(focusableSelectors)).filter(
    (el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"),
  )
}

export const CPopup = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = "sm",
  dismissible = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  overlayClassName,
  containerClassName,
  panelClassName,
  headerClassName,
  bodyClassName,
  footerClassName,
  rootId = DEFAULT_MODAL_ROOT_ID,
}: CPopupProps) => {
  const zIndex = useZIndex(isOpen)
  const panelRef = useRef<HTMLDivElement>(null)
  const lastFocusedElement = useRef<HTMLElement | null>(null)
  const titleId = useId()

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return
    const originalOverflow = document.body.style.overflow
    const originalPaddingRight = document.body.style.paddingRight

    document.body.style.overflow = "hidden"
    document.body.style.paddingRight = "0px"

    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.paddingRight = originalPaddingRight
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return

    lastFocusedElement.current = document.activeElement instanceof HTMLElement ? document.activeElement : null

    const timer = window.setTimeout(() => {
      const focusable = getFocusableElements(panelRef.current)
      if (focusable[0]) {
        focusable[0].focus()
      } else {
        panelRef.current?.focus()
      }
    }, 0)

    return () => {
      window.clearTimeout(timer)
      lastFocusedElement.current?.focus()
      lastFocusedElement.current = null
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !dismissible || !closeOnEscape) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        onClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, dismissible, closeOnEscape, onClose])

  const handleBackdropClick = () => {
    if (!dismissible || !closeOnBackdropClick) return
    onClose()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return

    const focusable = getFocusableElements(panelRef.current)
    if (focusable.length === 0) {
      event.preventDefault()
      panelRef.current?.focus()
      return
    }

    const activeElement = document.activeElement as HTMLElement | null
    const currentIndex = activeElement ? focusable.indexOf(activeElement) : -1

    if (event.shiftKey) {
      if (currentIndex <= 0) {
        focusable[focusable.length - 1]?.focus()
        event.preventDefault()
      }
      return
    }

    if (currentIndex === -1 || currentIndex === focusable.length - 1) {
      focusable[0]?.focus()
      event.preventDefault()
    }
  }

  // Looked up on every render rather than stored: a modal root that mounts after the popup is picked up on
  // the next render, and without one the popup goes to the document body.
  const portalRoot = typeof document === "undefined" || !rootId ? null : document.getElementById(rootId)
  const portalTarget = portalRoot ?? (typeof document === "undefined" ? null : document.body)

  if (!isOpen || !portalTarget) return null

  const overlayClasses = joinClasses("absolute inset-0 bg-black/50", overlayClassName)
  const containerClasses = joinClasses(
    "absolute inset-0 z-10 flex items-center justify-center px-4",
    containerClassName,
  )
  const panelClasses = joinClasses(
    `relative flex flex-col rounded-2xl bg-white shadow-xl focus:outline-none ${size !== "fit" ? "w-full" : ""}`,
    sizeClasses[size],
    "animate-in fade-in duration-200",
    panelClassName,
  )
  const headerClasses = joinClasses(
    "flex items-start justify-between gap-4 border-b border-transparent px-5 pt-5 pb-3",
    headerClassName,
  )
  const bodyClasses = joinClasses("px-5 pb-5 text-sm text-gray-700", bodyClassName)
  const footerClasses = joinClasses("border-t border-gray-100 px-5 pb-5 pt-4", footerClassName)

  const labelledById = title ? titleId : undefined
  const ariaLabel = title ? undefined : "Dialog"

  if (!zIndex) return null

  const popupContent = (
    <div
      className="fixed inset-0"
      style={{ zIndex }}
      role="presentation"
      // The Tab trap listens here, on the popup's root, for the keydowns that bubble up
      // from the focused control inside the panel — the panel is a dialog, not a widget.
      onKeyDown={handleKeyDown}
    >
      <div
        // Not a control: with the mouse it repeats the closing Escape already does (see closeOnEscape).
        role="presentation"
        className={overlayClasses}
        onClick={handleBackdropClick}
      />
      <div className={containerClasses}>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledById}
          aria-label={ariaLabel}
          ref={panelRef}
          tabIndex={-1}
          className={panelClasses}
        >
          <div className={headerClasses}>
            {title ? (
              <h3
                id={labelledById}
                className="text-base font-semibold text-gray-900"
              >
                {title}
              </h3>
            ) : (
              <span className="text-sm font-semibold text-gray-900">&nbsp;</span>
            )}
            {showCloseButton && dismissible ? (
              <button
                type="button"
                className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                onClick={onClose}
                aria-label="Close dialog"
              >
                <CloseIcon size={16} />
              </button>
            ) : null}
          </div>
          <div className={bodyClasses}>{children}</div>
          {footer ? <div className={footerClasses}>{footer}</div> : null}
        </div>
      </div>
    </div>
  )

  return createPortal(popupContent, portalTarget)
}
